// The Force-Cooling card's primary action — one per stage, or the "waiting on
// someone else" hint. Lifted out of Precool.tsx so the RELEASED arm can be
// tested without mounting the whole board.
//
// F05 UF-09 — a released dolly's next step is Tipping, and the card used to say
// so in plain text ("Ready — tip in Tipping"), leaving the operator to open the
// menu and find the page. For a viewer the route gate would let into Tipping it
// is now a link there. It carries the batch id (W0 D1): Tipping marks that dolly's
// card; with no run picked yet it opens plain. Anyone without the Tipping module — or while the
// gate's reads are still deciding (`canTip` false) — keeps the text, because
// RequireModule answers a denied link with a silent bounce to Home.
import { Link } from 'react-router-dom'
import { Button } from '@/components/ui'
import { ThermometerSnowflake, MoveRight, CheckCircle2, Layers } from 'lucide-react'

/** Tipping's address for one released dolly: Tipping scrolls to and marks that
 *  batch's card. Read back by `tippingBatchParam`. */
export function tippingBatchHref(batchId: string | null | undefined): string {
  return batchId ? `/tipping?batch=${encodeURIComponent(batchId)}` : '/tipping'
}

/** The batch a Tipping address names, or null: a hand-edited `?batch=abc` must
 *  not mark anything (the page then opens plain). */
export function tippingBatchParam(raw: string | null): string | null {
  return raw && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(raw) ? raw.toLowerCase() : null
}

export type StageKind = 'cool' | 'hold' | 'release'

// Flat status→button map.
// fallow-ignore-next-line complexity
export function StageAction({ status, batchId, canRelease, canTip, onAct }: {
  status: string
  /** The dolly's batch id: carried to Tipping so it opens on this dolly. */
  batchId?: string | null
  canRelease: boolean
  /** The viewer may open Tipping (useCanOpenModule('tipping') === true). */
  canTip: boolean
  onAct: (kind: StageKind) => void
}) {
  if (status === 'STAGING')    return <ActBtn icon={<ThermometerSnowflake size={14} />} label="Start cool" tone="sky"     onClick={() => onAct('cool')} />
  if (status === 'COOLING')    return <ActBtn icon={<MoveRight size={14} />}            label="To holding" tone="blue"    onClick={() => onAct('hold')} />
  if (status === 'IN_HOLDING') return canRelease
    ? <Button variant="primary" size="sm" icon={<CheckCircle2 size={14} />} onClick={() => onAct('release')}>Release</Button>
    : <span className="text-[10px] text-muted self-center">QC/manager releases</span>
  if (status === 'RELEASED') return canTip
    ? (
      // Same tab: Back returns to the board (a new tab loses an iOS
      // home-screen app's session — the UF-07 label link made the same call).
      <Link to={tippingBatchHref(batchId)} className={`${ACT_BASE} ${TONES.emerald}`}>
        <Layers size={14} />Tip in Tipping →
      </Link>
    )
    : <span className="text-[10px] text-muted self-center">Ready — tip in Tipping</span>
  return null
}

// 44px minimum touch target — cold/gloved hands on a phone (FC-25).
const ACT_BASE = 'flex items-center gap-1 text-xs px-3 min-h-[44px] rounded-lg border'
const TONES = {
  sky:     'bg-sky-900/40 text-sky-200 border-sky-700/50 hover:bg-sky-900/60',
  blue:    'bg-blue-900/40 text-blue-200 border-blue-700/50 hover:bg-blue-900/60',
  emerald: 'bg-emerald-900/40 text-emerald-200 border-emerald-700/50 hover:bg-emerald-900/60',
}

function ActBtn({ icon, label, tone, onClick }: { icon: React.ReactNode; label: string; tone: keyof typeof TONES; onClick: () => void }) {
  return (
    <button onClick={onClick} className={`${ACT_BASE} ${TONES[tone]}`}>
      {icon}{label}
    </button>
  )
}
